import { getUserData } from "@/auth/getUserData";
import ChannelInterface from "@/interfaces/channel.interface";
import ChatLastEntryInterface from "@/interfaces/inbox/chatLastEntry.interface";
// @ts-ignore
import i18n from "@/libs/i18n";
import { ActiveChat, ChatChannel } from "@/views/modules/messaging-inbox/useChat";
import { ChatsListChat, PlatformType, ProviderType } from "@saysimple-platform/dtos";
import { escapeRegExp, isEmpty } from "lodash";
import { stripHtml } from "string-strip-html";

export const resolveAutomaticSignature = (message: string, channel: ChannelInterface | ChatChannel) => {
    if (!getUserData().displayName || getUserData().displayName?.length === 0) {
        return message;
    }

    switch (channel?.settings?.find((setting) => setting.name === "automaticSignature")?.value) {
        case "append-agent-name":
            return `${message} ^${getUserData().displayName}`;
        case "prepend-agent-name-bold":
            return `*${getUserData().displayName}*\r\n${message}`;
        default:
            return message;
    }
};

// For the correct icon class names, please check https://fontawesome.com/v5.15/icons?d=gallery&p=2&s=brands
// Be sure that the icons are imported in "global-components.js".
export const resolvePlatformIcon = (
    provider: ProviderType | ChatChannel["provider"],
    platform: PlatformType | ChatChannel["platform"]
): string[] | PlatformType | ChatChannel["platform"] => {
    switch (platform?.toLowerCase()) {
        case "whatsapp":
            return ["fab", "whatsapp"];
        case "web":
            return ["fa", "comment"];
        case "messenger":
            return ["fab", "facebook-messenger"];
        case "business_messages":
            if (provider === "smooch") {
                return ["fab", "apple"];
            }
            if (provider === "messagebird") {
                return ["fab", "google"];
            }

            break;
        case "instagram":
            return ["fab", "instagram"];
        case "marktplaats":
            // https://fontawesome.com/kits/0d1577e3b4/icons/marktplaats
            return ["fak", "marktplaats"];
        case "email":
            return ["fa", "envelope"];
        case "direct_messages":
            return ["fab", "twitter"];
        case "test":
            return ["fas", "comment-lines"];
        case "telegram":
            return ["fab", "telegram"];
        case "sms":
            return ["far", "message-sms"];
    }

    return platform;
};

export const resolvePlatformIconSvgSrc = (provider?: ProviderType, platform?: PlatformType): string | undefined => {
    if (!provider || !platform) {
        return;
    }

    if (provider === "smooch" && platform === "business_messages") {
        return "business_chat";
    }

    return platform.toLowerCase();
};

export const supportsMessageStatusUpdates = (channel: ChatChannel): boolean => {
    if (!channel.provider || !channel.platform) {
        return false;
    }

    const provider = channel.provider;
    const platform = channel.platform;

    if (provider === "messagebird" && ["whatsapp", "instagram", "business_messages"].includes(platform)) {
        return true;
    }

    if (provider === "bird" && ["whatsapp", "instagram", "business_messages"].includes(platform)) {
        return true;
    }

    if (provider === "twilio" && platform === "whatsapp") {
        return true;
    }

    if (provider === "smooch" && platform === "whatsapp") {
        return true;
    }

    if (provider === "eazy" && platform === "whatsapp") {
        return true;
    }

    if (provider === "twitter" && platform === "direct_messages") {
        return true;
    }

    if (provider === "spryng") {
        return true;
    }

    if (provider === "whatsapp") {
        return true;
    }

    return provider === "test";
};

export const resolveProviderColor = (provider: ProviderType): string => {
    return provider.toLowerCase();
};

export const resolvePlatformColor = (platform: PlatformType): string => {
    return platform.toLowerCase();
};

export const resolvePlatformClassByChannel = (
    channel: ChatChannel
): { background: string; text: string; border: string } => {
    let platform = channel.platform?.toLowerCase();

    switch (channel.platform) {
        // For business messages Smooch is our Apple, and Messagebird our Google provider
        case "business_messages":
            if (channel.provider === "smooch") {
                platform = "apple_business_chat";
            } else if (channel.provider === "messagebird") {
                platform = "google_business_messages";
            }
    }

    return { background: `${platform}-bg`, text: `${platform}-text`, border: `${platform}-border` };
};

export const transformPlatformName = (platform: PlatformType): string | PlatformType => {
    switch (platform) {
        case "whatsapp":
            return "WhatsApp";
        case "web":
            return "Webchat";
        case "messenger":
            return "Messenger";
        case "business_messages":
            return "Business Messages";
        case "instagram":
            return "Instagram";
        case "email":
            return "Email";
        case "direct_messages":
            return "Direct Messages";
        case "test":
            return "Test";
        case "sms":
            return "Sms";
    }

    return platform;
};

export const getChannelSettingValue = (channel?: ChatChannel, settingName?: string): string | undefined => {
    if (!channel || !settingName) {
        return;
    }

    const setting = channel?.settings?.find((setting) => setting.name === settingName);
    return setting ? setting.value : undefined;
};

export const resolvePriorityVariant = (chat?: ActiveChat | ChatsListChat, channel?: ChatChannel) => {
    if (!chat) {
        return;
    }

    if (chat.contact?.isHighPriority) {
        return "warning";
    }

    if (!channel) {
        return;
    }

    const channelPriority = getChannelSettingValue(channel, "priority");
    if (channelPriority === "1") {
        return "warning";
    }
};

export const resolveLastEntryMessage = (entry: ChatLastEntryInterface, platform?: string): string => {
    if (!entry) {
        return "";
    }

    let message = entry.message;

    switch (platform?.toLowerCase()) {
        case "email":
            message = stripHtml(message, {
                stripTogetherWithTheirContents: ["script", "style", "xml", "title"],
            }).result;
            break;
    }

    switch (entry.type) {
        case "location":
            return i18n.t("Location");
        case "asset":
            return !isEmpty(message) ? message : i18n.t("Attachment");
    }

    return message;
};

/***
 * Returns the filesize limit for the given platform and provider
 */
export const resolveFilesizeLimit = (provider: ProviderType, platform: PlatformType, assetType?: string): number => {
    if (provider === "messagebird" || platform === "test" || provider === "bird") {
        return 64;
    } else if (provider === "whatsapp") {
        // NOTE: For limits see https://developers.facebook.com/docs/whatsapp/cloud-api/reference/media#supported-media-types
        const baseAssetType = assetType?.split("/")[0].toLowerCase();
        switch (baseAssetType) {
            case "video":
            case "audio":
                return 16;
            case "text":
            case "application":
                return 100;
            case "image":
            default:
                // Lowest common denominator is 5mb so default to that
                return 5;
        }
    } else {
        // Default to 5mb
        return 5;
    }
};

export const resolveWriteMessageMaxChars = (platform: PlatformType, provider: ProviderType) => {
    if (!platform) {
        return 0;
    }

    switch (platform) {
        case "whatsapp":
        case "test":
        case "telegram":
            return 4096;
        case "sms":
            return 1530; // Plain encoded messages (also for Spryng)
        case "messenger":
            return 20000;
        case "direct_messages":
            return 10000;
        case "instagram":
        case "web":
        case "business_messages":
        default:
            return 1000;
    }
};
